import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DifficultyDialogButton } from "@/components/ui/Difficulty";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { BOARD_THEMES } from "@/constants/boardColors";
import { loadEmojiFont } from "@/lib/utils";
import { useBoardSettingsStore } from "@/stores/boardSettingsStore";
import { useGameStore } from "@/stores/gameStore";
import { useTutorialStore } from "@/stores/tutorialStore";
import {
  SignedIn,
  SignedOut,
  SignInButton,
  SignUpButton,
  UserButton,
  useUser,
} from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import {
  createRootRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import LogRocket from "logrocket";
import { ArrowLeft, ClipboardList, Menu, Moon, Sun } from "lucide-react";
import { useEffect } from "react";

const EMOJI_FONT_OPTIONS = [
  { value: "apple", label: "Apple Emoji" },
  { value: "google", label: "Google Emoji" },
  { value: "windows", label: "Windows Emoji" },
  { value: "twitter", label: "Twitter Emoji" },
  { value: "open", label: "OpenMoji" },
];

function RootComponent() {
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  // Get tutorial state
  const isTutorialActive = useTutorialStore((state) => state.isActive);

  // Check if we're in game or tutorial routes
  const isInGameOrTutorial = currentPath === "/game" || isTutorialActive;

  const darkMode = useBoardSettingsStore((state) => state.darkMode);
  const { user, isLoaded } = useUser();

  // Get board settings
  const {
    boardTheme,
    showCoordinates,
    showLegalMoves,
    showSquareNotation,
    enableAnimations,
    animationSpeed,
    emojiFont,
    setDarkMode,
    setBoardTheme,
    setShowCoordinates,
    setShowLegalMoves,
    setShowSquareNotation,
    setEnableAnimations,
    setAnimationSpeed,
    setEmojiFont,
  } = useBoardSettingsStore();

  // Get move history from game store
  const moveHistory = useGameStore((state) => state.moveHistory);

  // Apply dark mode to document element
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  // Set user context in Sentry, LogRocket, and Google Analytics when user is loaded
  useEffect(() => {
    if (isLoaded) {
      if (user) {
        // User is signed in, set Sentry user context
        Sentry.setUser({
          id: user.id,
          email: user.primaryEmailAddress?.emailAddress,
          username: user.username || undefined,
          firstName: user.firstName,
          lastName: user.lastName,
        });

        // User is signed in, identify user in LogRocket
        const logRocketTraits: Record<string, string | number | boolean> = {
          name:
            `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
            user.username ||
            "Unknown User",
        };

        if (user.primaryEmailAddress?.emailAddress) {
          logRocketTraits.email = user.primaryEmailAddress.emailAddress;
        }
        if (user.username) {
          logRocketTraits.username = user.username;
        }
        if (user.firstName) {
          logRocketTraits.firstName = user.firstName;
        }
        if (user.lastName) {
          logRocketTraits.lastName = user.lastName;
        }

        LogRocket.identify(user.id, logRocketTraits);

        // User is signed in, set Google Analytics user properties
        if (typeof window !== "undefined" && window.gtag) {
          window.gtag("config", "G-GEN67C812L", {
            user_id: user.id,
            custom_map: {
              user_email: user.primaryEmailAddress?.emailAddress,
              user_name:
                `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                user.username ||
                "Unknown User",
              username: user.username,
            },
          });

          // Set user properties
          window.gtag("set", {
            user_id: user.id,
            user_properties: {
              user_email: user.primaryEmailAddress?.emailAddress,
              user_name:
                `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                user.username ||
                "Unknown User",
              username: user.username,
              first_name: user.firstName,
              last_name: user.lastName,
            },
          });

          // Track user sign-in event
          window.gtag("event", "login", {
            method: "clerk",
            user_id: user.id,
          });
        }
      } else {
        // User is signed out, clear Sentry user context
        Sentry.setUser(null);

        // User is signed out, clear LogRocket identification
        LogRocket.identify("anonymous");

        // User is signed out, clear Google Analytics user data
        if (typeof window !== "undefined" && window.gtag) {
          window.gtag("config", "G-GEN67C812L", {
            user_id: null,
          });
        }
      }
    }
  }, [user, isLoaded]);

  useEffect(() => {
    loadEmojiFont(emojiFont);
  }, [emojiFont]);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);

    // Update document class for dark mode
    if (newDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  const handleBackClick = () => {
    navigate({ to: "/character-select" });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b shadow-sm p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {isInGameOrTutorial ? (
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackClick}
                aria-label="Go back to character select"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-lg font-bold">EmojiChess</h1>
            </div>
          ) : (
            <Link
              to="/character-select"
              className="text-xl font-bold text-foreground hover:text-foreground/80 transition-colors"
            >
              EmojiChess
            </Link>
          )}

          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              aria-label={
                darkMode ? "Switch to light mode" : "Switch to dark mode"
              }
            >
              {darkMode ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>

            {isInGameOrTutorial && (
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="View move history"
                  >
                    <ClipboardList className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Move History</SheetTitle>
                    <SheetDescription>
                      View the history of moves in this game
                    </SheetDescription>
                  </SheetHeader>

                  <div className="space-y-4 p-4">
                    <div className="flex justify-between items-center mb-2">
                      <label className="flex items-center gap-2">
                        <Checkbox
                          id="show-legal-moves-history"
                          checked={showLegalMoves}
                          onCheckedChange={(checked) =>
                            setShowLegalMoves(checked as boolean)
                          }
                        />
                        <span className="text-sm font-medium leading-none">
                          Show Legal Moves
                        </span>
                      </label>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto border rounded p-2">
                      {moveHistory.length === 0 ? (
                        <p className="text-sm text-muted-foreground p-2">
                          No moves yet
                        </p>
                      ) : (
                        moveHistory.map((move, index) => (
                          <div key={index} className="flex gap-2 p-1">
                            <span className="font-medium">
                              {Math.floor(index / 2) + 1}.
                            </span>
                            <span>{move.san}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            )}

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open settings">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Board Settings</SheetTitle>
                  <SheetDescription>
                    Customize your chess board appearance
                  </SheetDescription>
                </SheetHeader>

                <div className="space-y-4 p-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="dark-mode"
                      checked={darkMode}
                      onCheckedChange={(checked) => {
                        setDarkMode(checked as boolean);
                        if (checked) {
                          document.documentElement.classList.add("dark");
                        } else {
                          document.documentElement.classList.remove("dark");
                        }
                      }}
                    />
                    <label
                      htmlFor="dark-mode"
                      className="text-sm font-medium leading-none"
                    >
                      Dark Mode
                    </label>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none">
                      Board Theme
                    </label>
                    <Select
                      value={BOARD_THEMES.findIndex(
                        (theme) => theme.name === boardTheme.name
                      ).toString()}
                      onValueChange={(value) =>
                        setBoardTheme(BOARD_THEMES[parseInt(value)])
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select theme" />
                      </SelectTrigger>
                      <SelectContent>
                        {BOARD_THEMES.map((theme, index) => (
                          <SelectItem key={theme.name} value={index.toString()}>
                            {theme.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="show-coordinates"
                      checked={showCoordinates}
                      onCheckedChange={(checked) =>
                        setShowCoordinates(checked as boolean)
                      }
                    />
                    <label
                      htmlFor="show-coordinates"
                      className="text-sm font-medium leading-none"
                    >
                      Show Coordinates
                    </label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="show-legal-moves"
                      checked={showLegalMoves}
                      onCheckedChange={(checked) =>
                        setShowLegalMoves(checked as boolean)
                      }
                    />
                    <label
                      htmlFor="show-legal-moves"
                      className="text-sm font-medium leading-none"
                    >
                      Show Legal Moves
                    </label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="show-square-notation"
                      checked={showSquareNotation}
                      onCheckedChange={(checked) =>
                        setShowSquareNotation(checked as boolean)
                      }
                    />
                    <label
                      htmlFor="show-square-notation"
                      className="text-sm font-medium leading-none"
                    >
                      Show Square Notation
                    </label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="enable-animations"
                      checked={enableAnimations}
                      onCheckedChange={(checked) =>
                        setEnableAnimations(checked as boolean)
                      }
                    />
                    <label
                      htmlFor="enable-animations"
                      className="text-sm font-medium leading-none"
                    >
                      Enable Animations
                    </label>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none">
                      Animation Speed
                    </label>
                    <Select
                      value={animationSpeed}
                      onValueChange={(value) =>
                        setAnimationSpeed(value as "slow" | "normal" | "fast")
                      }
                      disabled={!enableAnimations}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select speed" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="slow">Slow</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="fast">Fast</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none">
                      Emoji Font
                    </label>
                    <Select value={emojiFont} onValueChange={setEmojiFont}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select emoji font" />
                      </SelectTrigger>
                      <SelectContent>
                        {EMOJI_FONT_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="pt-2">
                    <DifficultyDialogButton />
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            <SignedOut>
              <SignInButton mode="modal">
                <button className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-md transition-colors">
                  Sign In
                </button>
              </SignInButton>
              <SignUpButton mode="modal">
                <button className="bg-secondary hover:bg-secondary/80 text-secondary-foreground px-4 py-2 rounded-md transition-colors">
                  Sign Up
                </button>
              </SignUpButton>
            </SignedOut>
            <SignedIn>
              <UserButton
                appearance={{
                  elements: {
                    avatarBox: "w-8 h-8",
                  },
                }}
              />
            </SignedIn>
          </div>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t bg-black py-2 z-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-center items-center gap-4 text-xs text-gray-400">
            <Link
              to="/privacy-policy"
              className="hover:text-gray-600 transition-colors"
            >
              Privacy Policy
            </Link>
            <span className="text-gray-300">•</span>
            <Link
              to="/terms-of-service"
              className="hover:text-gray-600 transition-colors"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
});

